<template>
    <div class="table-container w-full">
        <div class="list-top-container">
            <div class="list-search-container w-full mb-2 md:w-auto md:mb-0">
                <a-input-search
                    data-cy="listmanager-meta-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
            <div class="list-buttons w-full mb-2 md:w-auto md:mb-0">
                <a-dropdown :trigger="['click']" v-if="recordsSelected" class="mr-4">
                    <a-button @click="(e) => e.preventDefault()" data-cy="btn-mass-selection">
                        {{ $t('admin.list_manager_meta.table.buttons.actions') }}
                        <DownOutlined />
                    </a-button>
                    <template #overlay>
                        <a-menu @click="onActionButtons">
                            <a-menu-item key="delete" data-cy="actions-delete-meta">
                                {{ $t('admin.list_manager_meta.table.buttons.delete') }}
                            </a-menu-item>
                        </a-menu>
                    </template>
                </a-dropdown>
                <a-button-group>
                    <a-button @click="onAdd" data-cy="btn-create-meta" type="primary">
                        {{ $t('admin.list_manager_meta.table.buttons.add') }}
                    </a-button>
                </a-button-group>
            </div>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="list-manager-meta-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.category"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    :rowSelection="{ selectedRowKeys: selected_row_keys, onChange: onSelectChange }"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'actions'">
                            <a-button-group>
                                <a-tooltip placement="top" :title="$t('admin.list_manager_meta.table.buttons.edit')">
                                    <a-button
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        @click="onTableAction(record.category, 'edit')"
                                        :disabled="recordsSelected"
                                    >
                                        <EditOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip placement="top" :title="$t('admin.list_manager_meta.table.buttons.delete')">
                                    <a-button
                                        class="text-default-red hover:text-default-red focus:text-default-red"
                                        data-cy="btn-meta-delete-single"
                                        @click="onTableAction(record.category, 'delete')"
                                        :disabled="recordsSelected"
                                    >
                                        <DeleteOutlined />
                                    </a-button>
                                </a-tooltip>
                            </a-button-group>
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { DeleteOutlined, DownOutlined, EditOutlined } from '@ant-design/icons-vue';

export default {
    components: { DeleteOutlined, DownOutlined, EditOutlined },
    data() {
        return {
            data: [],
            search_value: '',
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
            list_manager_meta: {},
        };
    },
    props: {
        selected_row_keys: Array,
    },
    mounted() {
        this.fetch();
    },
    computed: {
        getColumns() {
            return [
                {
                    key: 'actions',
                    width: 35,
                },
                {
                    title: this.$t('globals.tables.list_manager_meta.headers.category'),
                    key: 'category',
                    dataIndex: 'category',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.list_manager_meta.headers.list_name'),
                    key: 'list_name',
                    dataIndex: 'list_name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.list_manager_meta.headers.list_description'),
                    key: 'list_description',
                    dataIndex: 'list_description',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
        recordsSelected() {
            return this.selected_row_keys.length > 0;
        },
    },
    methods: {
        fetch(params = {}) {
            this.loading = true;
            this.params = params;

            this.$http
                .get('/api/listmanagermeta', { params: params })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVLMMF01' }),
                        });
                    }
                });
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
        onSelectChange(selected_row_keys, selected_rows) {
            this.$emit('onSelectChange', selected_row_keys);
        },
        onTableAction(id, key) {
            switch (key) {
                case 'edit':
                    this.$emit('onTableAction', {
                        id: id,
                        key: key,
                    });
                    break;
                case 'delete':
                    this.onDelete(id);
                    break;
            }
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                this.search_value = input;

                this.onSelectChange([]);

                this.fetch({
                    search_value: this.search_value,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        onAdd() {
            this.$emit('onTableAction', {
                id: null,
                key: 'edit',
            });
        },
        onActionButtons(event, type) {
            switch (event.key) {
                case 'delete':
                    this.onDelete();
                    break;
            }
        },
        onDelete(meta_id = '') {
            let ids = [];

            if (meta_id !== '' && meta_id !== undefined) {
                ids = [meta_id];
            } else if (this.selected_row_keys.length >= 1) {
                ids = this.selected_row_keys;
            }

            let that = this;

            this.$confirm({
                title: this.$t('admin.list_manager_meta.confirm.delete'),
                content: '',
                onOk() {
                    that.$http
                        .post('/api/listmanagermeta/delete', {
                            ids: ids,
                        })
                        .then((response) => {
                            if (response.data.status == 'success') {
                                that.$notification.success({
                                    message: response.data.message,
                                });

                                that.onSelectChange([]);

                                that.reload();
                            } else {
                                that.$notification.error({
                                    message: response.data.message,
                                });
                            }
                        })
                        .catch((error) => {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVLMMD01' }),
                            });
                        })
                        .finally(() => {
                            that.processing = false;
                        });
                },
            });
        },
    },
};
</script>
